class SocialFloating extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing',
      atFooterClass: 'at-footer'
    };

    this.querySelectorAll('[data-social-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });

    // Add footer detection
    this.isScrolling = false;
    this.onScrollHandler = this.throttledScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.onScrollHandler(); // Check initial position
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  throttledScroll() {
    if (!this.isScrolling) {
      requestAnimationFrame(() => {
        this.onScroll();
        this.isScrolling = false;
      });
      this.isScrolling = true;
    }
  }

  onScroll() {
    // Target the main site footer specifically
    const footer = document.querySelector('.shopify-section-group-footer-group footer, footer.site-footer, body > footer:last-of-type');

    if (!footer) return;

    const popupFloating = this.closest('.popup-floating');
    if (!popupFloating) return;

    // Check if this is a "star" product page where popup should stay visible
    const keepVisibleAtFooter = this.dataset.keepVisibleAtFooter === 'true';

    const footerRect = footer.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Check if footer is visible in viewport
    const footerVisible = footerRect.top < windowHeight;

    if (footerVisible && !keepVisibleAtFooter) {
      popupFloating.classList.add(this.classes.atFooterClass);
    } else {
      popupFloating.classList.remove(this.classes.atFooterClass);
    }

    // Update social panel position if it's open (mobile dock may have appeared/disappeared)
    if (this.classList.contains(this.classes.activeClass)) {
      this.updateWrapperPosition();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!theme.config.mqlSmall) return;

    this.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  updateWrapperPosition() {
    const wrapper = this.querySelector('.social-floating-wrapper');
    if (!wrapper) return;

    const mobileDock = document.querySelector('.mobile-dock.is-active');
    if (mobileDock) {
      const dockHeight = mobileDock.offsetHeight;
      wrapper.style.bottom = dockHeight + 'px';
    } else {
      wrapper.style.bottom = '0';
    }
  }

  open() {
    this.classList.add(this.classes.activeClass);

    // Position the wrapper above the mobile dock if it's visible
    this.updateWrapperPosition();

    // close promo-popup
    const popup = document.querySelector('promo-popup');
    popup && popup.close();
  }

  close() {
    this.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.classList.remove(this.classes.activeClass);
      this.classList.remove(this.classes.closingClass);
    }, 500);
  }
}
customElements.define('social-floating', SocialFloating);

class PromoPopup extends HTMLElement {
  constructor() {
    super();

    // Prevent popup on Shopify robot challenge page
    if (window.location.pathname === '/challenge') {
      return;
    }

    this.cookieName = 'beyours:promo-popup';

    this.classes = {
      bodyClass: 'promo-popup--open',
      activeClass: 'is-active',
      closingClass: 'is-closing',
      showImage: 'show-image'
    };

    this.popup = this.querySelector('.promo-popup');
    this.overlay = this.querySelector('.popup-overlay');
    this.popupContent = this.querySelector('.popup');

    // Store scroll position for restoration
    this.scrollPosition = 0;

    // Track if user has manually interacted with popup
    this.hasUserInteracted = false;
    this.delayedPopupTimer = null;

    // Open modal if errors or success message exist
    if (this.querySelector('.form__message')) {
      this.open();
    }

    this.querySelectorAll('[data-popup-toggle]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
      // Faster touch response for close button on mobile
      button.addEventListener('touchend', (e) => {
        // Only handle if it's the close button (not overlay which has its own handler)
        if (button.classList.contains('popup-close')) {
          e.preventDefault();
          this.onButtonClick(e);
        }
      }, { passive: false });
    });

    // Enhanced: Add click outside to close functionality
    this.setupClickOutsideToClose();
    
    // Enhanced: Add escape key functionality
    this.setupEscapeKey();
    
    // Enhanced: Setup overlay hover effects
    this.setupOverlayEffects();

    if (!this.getCookie(this.cookieName) || this.dataset.testMode === 'true') {
      this.init();
    }

    // Enhanced: Setup AJAX form submission to prevent page reload
    this.setupAjaxForm();
  }

  // Enhanced: AJAX newsletter form submission
  setupAjaxForm() {
    const form = this.querySelector('form.popup__newsletter');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const submitButton = form.querySelector('button[type="submit"]');
      const emailInput = form.querySelector('input[type="email"]');
      const formField = form.querySelector('.field');

      // Basic email validation before submission
      const email = emailInput?.value?.trim();
      if (!email || !this.isValidEmail(email)) {
        this.showFormError(form, formField, emailInput,
          window.theme?.strings?.newsletter_error || 'Please enter a valid email address');
        return;
      }

      // Disable button during submission
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('loading');
      }

      // Clear any existing messages
      const existingMessages = form.querySelectorAll('.form__message');
      existingMessages.forEach(msg => msg.remove());
      if (emailInput) emailInput.classList.remove('invalid');

      try {
        const formData = new FormData(form);
        const response = await fetch(form.action, {
          method: 'POST',
          body: formData
        });

        // Shopify returns 200 OK for successful submissions
        // and 422 for validation errors
        if (response.ok) {
          // Success - show thank you message
          // (This includes "already subscribed" - the email IS in the list which is the goal)
          this.showFormSuccess(form, formField);
        } else {
          // Server returned an error status
          const responseText = await response.text();

          // Try to extract a meaningful error message
          let errorMsg = 'Something went wrong. Please try again.';

          // Check for invalid email format errors from Shopify
          if (responseText.includes('valid email') || responseText.includes('invalid')) {
            errorMsg = 'Please enter a valid email address';
          }

          this.showFormError(form, formField, emailInput, errorMsg);
        }
      } catch (error) {
        // Network error
        this.showFormError(form, formField, emailInput,
          'Connection error. Please try again.');
      } finally {
        // Re-enable button
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('loading');
        }
      }
    });
  }

  // Helper: Validate email format
  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Helper: Show form success message
  showFormSuccess(form, formField) {
    const successMessage = document.createElement('p');
    successMessage.className = 'form__message h3';
    successMessage.innerHTML = `
      <svg class="icon icon-success" aria-hidden="true" focusable="false">
        <use href="#icon-success"></use>
      </svg>
      ${window.theme?.strings?.newsletter_success || 'Thanks for subscribing'}
    `;

    // Hide the form field and show success
    if (formField) formField.style.display = 'none';
    const newsletterText = form.querySelector('.newsletter__text');
    if (newsletterText) newsletterText.style.display = 'none';

    form.insertBefore(successMessage, form.firstChild);

    // Close popup after delay
    setTimeout(() => {
      this.close();
    }, 2500);
  }

  // Helper: Show form error message
  showFormError(form, formField, emailInput, message) {
    // Clear any existing messages first
    const existingMessages = form.querySelectorAll('.form__message');
    existingMessages.forEach(msg => msg.remove());

    const errorMessage = document.createElement('small');
    errorMessage.className = 'form__message errors';
    errorMessage.innerHTML = `
      <svg class="icon icon-error" aria-hidden="true" focusable="false">
        <use href="#icon-error"></use>
      </svg>
      ${message}
    `;

    if (formField) {
      formField.insertBefore(errorMessage, formField.firstChild);
    }

    if (emailInput) {
      emailInput.classList.add('invalid');
      emailInput.focus();
    }
  }

  // Enhanced: Click outside to close functionality
  setupClickOutsideToClose() {
    if (this.overlay) {
      // Use both click and touchend for faster mobile response
      const closeHandler = (e) => {
        if (e.target === this.overlay) {
          e.preventDefault();
          this.close();
        }
      };

      this.overlay.addEventListener('click', closeHandler);

      // Faster touch response on mobile
      this.overlay.addEventListener('touchend', (e) => {
        if (e.target === this.overlay) {
          e.preventDefault();
          this.close();
        }
      }, { passive: false });
    }

    // Prevent popup content clicks/touches from closing popup
    if (this.popupContent) {
      this.popupContent.addEventListener('click', (e) => {
        e.stopPropagation();
      });
      this.popupContent.addEventListener('touchend', (e) => {
        e.stopPropagation();
      });
    }
  }

  // Enhanced: Escape key functionality
  setupEscapeKey() {
    this.escapeKeyHandler = (e) => {
      if (e.key === 'Escape' && this.popup && this.popup.classList.contains(this.classes.activeClass)) {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeKeyHandler);
  }

  // Enhanced: Overlay hover effects with large X
  setupOverlayEffects() {
    if (!this.overlay) return;

    let hoverTimeout;
    
    this.overlay.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      this.overlay.style.background = 'rgba(0, 0, 0, 0.7)';
    });

    this.overlay.addEventListener('mouseleave', () => {
      hoverTimeout = setTimeout(() => {
        this.overlay.style.background = 'rgba(0, 0, 0, 0.6)';
      }, 100);
    });
  }

  // Enhanced: Better scroll position management
  preserveScrollPosition() {
    this.scrollPosition = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  }

  restoreScrollPosition() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this.scrollPosition);
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }

  disconnectedCallback() {
    // Clean up delayed popup timer
    if (this.delayedPopupTimer) {
      clearTimeout(this.delayedPopupTimer);
      this.delayedPopupTimer = null;
    }

    // Enhanced: Clean up event listeners
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
    }

    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      document.body.classList.remove(this.classes.bodyClass);
    }
  }

  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  init() {
    if(!this.popup) {
      return;
    }

    if (Shopify && Shopify.designMode) {
      return;
    }

    // Store timer reference so it can be cancelled if user interacts first
    this.delayedPopupTimer = setTimeout(function() {
      // Only show if user hasn't already manually interacted with popup
      if (!this.hasUserInteracted) {
        this.open();
      }
      this.delayedPopupTimer = null;
    }.bind(this), parseInt(this.dataset.delay) * 1000);
  }

  onButtonClick(event) {
    event.preventDefault();

    // Mark that user has manually interacted - cancel any pending delayed popup
    this.hasUserInteracted = true;
    if (this.delayedPopupTimer) {
      clearTimeout(this.delayedPopupTimer);
      this.delayedPopupTimer = null;
    }

    this.popup.classList.contains(this.classes.activeClass) ? this.close() : this.open();
  }

  open() {
    document.body.classList.remove(this.classes.bodyClass);
    this.popup.classList.add(this.classes.activeClass);
    setTimeout(() => {
      this.popup.classList.add(this.classes.showImage);
    }, 600);

    if (this.popup.dataset.position === 'center') {
      setScrollbarWidth();
      document.body.classList.add(this.classes.bodyClass);
      // Enhanced: Better scroll position management
      this.preserveScrollPosition();
    }

    // close social-floating
    const social = document.querySelector('social-floating');
    social && social.close();
  }

  close() {
    this.popup.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.popup.classList.remove(this.classes.activeClass);
      this.popup.classList.remove(this.classes.closingClass);
      this.popup.classList.remove(this.classes.showImage);

      if (this.popup.dataset.position === 'center') {
        document.body.classList.remove(this.classes.bodyClass);
        // Enhanced: Restore scroll position
        this.restoreScrollPosition();
      }
    }, 500);

    // Remove a cookie in case it was set in test mode
    if (this.dataset.testMode === 'true') {
      this.removeCookie(this.cookieName);
      return;
    }

    this.setCookie(this.cookieName, this.dataset.expiry);
  }

  getCookie(name) {
    const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    return match ? match[2] : null;
  }

  setCookie(name, expiry) {
    document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
  }

  removeCookie(name) {
    document.cookie = `${name}=; max-age=0`;
  }
}
customElements.define('promo-popup', PromoPopup);

// Enhanced: Additional modern popup functionality
document.addEventListener('DOMContentLoaded', function() {
  // Enhanced close button interactions
  const closeButtons = document.querySelectorAll('.popup-close');
  closeButtons.forEach(button => {
    // Add enhanced hover effects
    button.addEventListener('mouseenter', function() {
      this.style.transform = 'scale(1.1)';
      this.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.15)';
    });
    
    button.addEventListener('mouseleave', function() {
      this.style.transform = 'scale(1)';
      this.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
    });
  });

  // Enhanced popup animations with better timing
  const popups = document.querySelectorAll('.promo-popup');
  popups.forEach(popup => {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isActive = popup.classList.contains('is-active');
          const isClosing = popup.classList.contains('is-closing');
          
          if (isActive && !isClosing) {
            // Enhanced opening animation
            popup.style.animation = 'fade-in 0.4s ease-out';
            const wrapper = popup.querySelector('.popup-wrapper');
            if (wrapper) {
              wrapper.style.animation = 'popup-in 0.4s ease-out';
            }
          }
        }
      });
    });
    
    observer.observe(popup, { attributes: true });
  });
});